import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AppBranding from './AppBranding';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { 
  ArrowLeft, 
  Users, 
  Activity, 
  TrendingUp, 
  TrendingDown,
  Circle,
  RefreshCw,
  User,
  Copy,
  X,
  Play
} from 'lucide-react';
import { Toaster, toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_V2 = `${BACKEND_URL}/api/v2`;

const UsersPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedUserSettings, setSelectedUserSettings] = useState(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [restartingUser, setRestartingUser] = useState(null);

  const checkAdminStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API_V2}/system/admin/check`, { withCredentials: true });
      setIsAdmin(response.data.is_admin || false);
    } catch (error) {
      console.error('Failed to check admin status:', error);
      setIsAdmin(false);
    }
  }, []);

  const handleRestartBot = async (userId, userName) => {
    if (!isAdmin) return;
    
    setRestartingUser(userId);
    try {
      const response = await axios.post(`${API_V2}/user/bot/admin/restart/${userId}`, {}, { withCredentials: true });
      if (response.data.success) {
        toast.success(`Bot restarted for ${userName}`);
        fetchUsers(); // Refresh the list
      } else {
        toast.error(response.data.message || 'Failed to restart bot');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to restart bot: ${errorMsg}`);
    } finally {
      setRestartingUser(null);
    }
  };

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_V2}/auth/users`, {
        withCredentials: true
      });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to load users: ${errorMsg}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    checkAdminStatus();
    // Auto-refresh every 22 seconds (25% faster than 30 seconds)
    const interval = setInterval(fetchUsers, 22000);
    return () => clearInterval(interval);
  }, [fetchUsers, checkAdminStatus]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchUsers();
  };

  const handleViewSettings = (user) => {
    setSelectedUserSettings(user);
    setShowSettingsModal(true);
  };

  const handleCopySettings = async () => {
    if (!selectedUserSettings) return;
    
    try {
      // Copy the selected user's settings to current user
      const settingsToCopy = {
        rsi_oversold: selectedUserSettings.settings?.rsi_oversold || 25,
        rsi_overbought: selectedUserSettings.settings?.rsi_overbought || 75,
        trade_percent: selectedUserSettings.settings?.trade_percent || 10,
        no_loss_sell_enabled: selectedUserSettings.settings?.no_loss_sell_enabled !== false,
        concentration_limit_enabled: selectedUserSettings.settings?.concentration_limit_enabled !== false,
        concentration_limit_percent: selectedUserSettings.settings?.concentration_limit_percent || 75,
      };
      
      await axios.post(`${API_V2}/user/settings/update`, settingsToCopy, { withCredentials: true });
      toast.success(`Settings copied from ${selectedUserSettings.name}!`);
      setShowSettingsModal(false);
    } catch (error) {
      toast.error('Failed to copy settings', { description: error.response?.data?.detail || error.message });
    }
  };

  const formatProfit = (value) => {
    if (value === 0) return '$0.00';
    const prefix = value >= 0 ? '+' : '';
    return `${prefix}$${value.toFixed(4)}`;
  };

  const getProfitColor = (value) => {
    if (value > 0) return 'text-[#00FF41]';
    if (value < 0) return 'text-red-500';
    return 'text-[#008F11]';
  };

  return (
    <div className="min-h-screen">
      <AppBranding />
      <div className="p-3 md:p-4">
      <Toaster position="top-right" theme="dark" />
      
      {/* Header */}
      <header className="mb-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3 md:gap-4 flex-wrap">
            <Button
              onClick={() => navigate('/dashboard')}
              className="matrix-btn rounded-none text-xs h-8"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">BACK TO DASHBOARD</span>
              <span className="sm:hidden">BACK</span>
            </Button>
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 md:h-6 md:w-6 text-[#00FF41]" />
              <h1 className="text-lg md:text-2xl font-bold text-[#00FF41] tracking-widest">
                ALL TRADERS
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="border-[#00FF41] text-[#00FF41]">
              {users.length} USERS
            </Badge>
            <Button
              onClick={handleRefresh}
              className="matrix-btn rounded-none text-xs h-8"
              disabled={refreshing}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`h-3 w-3 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              REFRESH
            </Button>
          </div>
        </div>
      </header>

      {/* Users List */}
      <Card className="matrix-card rounded-none border border-[#003B00]">
        <CardHeader className="border-b border-[#003B00] pb-3">
          <CardTitle className="text-[#00FF41] text-sm tracking-widest flex items-center gap-2">
            <Activity className="h-4 w-4" />
            TRADERS RANKED BY PROFIT/HR
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-pulse text-[#00FF41] text-lg tracking-widest">
                LOADING TRADERS...
              </div>
            </div>
          ) : users.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-[#008F11] text-sm">
                No traders found
              </div>
            </div>
          ) : (
            <ScrollArea className="h-[calc(100vh-250px)]">
              <div className="divide-y divide-[#003B00]">
                {/* Table Header — desktop only */}
                <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 bg-[#001500] text-[#008F11] text-xs tracking-wider">
                  <div className="col-span-1">#</div>
                  <div className="col-span-2">TRADER</div>
                  <div className="col-span-1 text-center">STATUS</div>
                  <div className="col-span-2 text-right">PORTFOLIO</div>
                  <div className="col-span-2 text-right">PROFIT/HR</div>
                  <div className="col-span-2 text-center">PROFIT BY COIN</div>
                  <div className="col-span-2 text-center">SETTINGS</div>
                </div>
                
                {/* User Rows */}
                {users.map((u, index) => (
                  <div 
                    key={u.user_id} 
                    className={`px-3 md:px-4 py-3 hover:bg-[#001500]/50 transition-colors ${
                      u.is_trading ? 'bg-[#003B00]/20' : ''
                    }`}
                    data-testid={`user-row-${index}`}
                  >
                    {/* MOBILE LAYOUT — stacked card (hidden md:hidden) */}
                    <div className="md:hidden flex flex-col gap-2">
                      {/* Row 1: rank + avatar + name + status */}
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-mono w-7 shrink-0 ${index === 0 ? 'text-yellow-500' : index === 1 ? 'text-gray-400' : index === 2 ? 'text-amber-600' : 'text-[#008F11]'}`}>
                          #{index + 1}
                        </span>
                        <div className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/profile/${u.user_id}`)} data-testid={`user-profile-link-mobile-${index}`}>
                          {u.picture ? (
                            <img src={u.picture} alt={u.name} className="h-8 w-8 rounded-full border border-[#003B00] shrink-0" />
                          ) : (
                            <div className="h-8 w-8 rounded-full border border-[#003B00] flex items-center justify-center bg-[#001500] shrink-0">
                              <User className="h-4 w-4 text-[#008F11]" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="text-[#00FF41] text-sm font-medium truncate hover:underline">
                              {u.name || 'Unknown'}
                            </div>
                            <div className="text-[10px] flex items-center gap-2">
                              <span className={u.mode === 'live' ? 'text-red-500 font-bold' : 'text-blue-400 font-bold'}>
                                {u.mode?.toUpperCase() || 'SIM'}
                              </span>
                              {u.is_trading ? (
                                <span className="inline-flex items-center gap-1 text-[#00FF41]">
                                  <Circle className="h-2 w-2 fill-[#00FF41] text-[#00FF41] animate-pulse" />
                                  LIVE
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-[#666]">
                                  <Circle className="h-2 w-2 fill-[#003B00] text-[#003B00]" />
                                  IDLE
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        {/* Admin restart on mobile (compact) */}
                        {isAdmin && !u.is_trading && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleRestartBot(u.user_id, u.name); }}
                            disabled={restartingUser === u.user_id}
                            className="h-7 w-7 p-0 text-yellow-500 hover:text-yellow-400 hover:bg-yellow-900/20 shrink-0"
                            title="Restart this user's bot"
                          >
                            {restartingUser === u.user_id ? (
                              <RefreshCw className="h-3 w-3 animate-spin" />
                            ) : (
                              <Play className="h-3 w-3" />
                            )}
                          </Button>
                        )}
                      </div>
                      
                      {/* Row 2: stats grid 2x2 */}
                      <div className="grid grid-cols-2 gap-2 pl-9 text-[11px]">
                        <div className="bg-[#000] border border-[#003B00] px-2 py-1.5">
                          <div className="text-[#008F11] text-[9px] uppercase tracking-wider">Portfolio</div>
                          <div className="text-[#00FF41] font-mono text-xs truncate">
                            ${u.portfolio_value?.toLocaleString() || '1,000.00'}
                          </div>
                        </div>
                        <div className="bg-[#000] border border-[#003B00] px-2 py-1.5">
                          <div className="text-[#008F11] text-[9px] uppercase tracking-wider">Profit/Hr</div>
                          <div className={`font-mono text-xs flex items-center gap-1 truncate ${getProfitColor(u.profit_per_hour || 0)}`}>
                            {(u.profit_per_hour || 0) > 0 ? (
                              <TrendingUp className="h-3 w-3 shrink-0" />
                            ) : (u.profit_per_hour || 0) < 0 ? (
                              <TrendingDown className="h-3 w-3 shrink-0" />
                            ) : null}
                            <span className="truncate">{formatProfit(u.profit_per_hour || 0)}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Row 3: profit by coin badges */}
                      {u.profit_by_coin && Object.keys(u.profit_by_coin).length > 0 && (
                        <div className="flex flex-wrap gap-1.5 pl-9">
                          {Object.entries(u.profit_by_coin).map(([coin, profit]) => (
                            <div key={coin} className="border border-[#003B00] bg-[#000] px-1.5 py-0.5 flex items-center gap-1 text-[10px]">
                              <span className="text-[#666] font-mono">{coin.replace('-USD', '')}</span>
                              <span className={`font-mono ${getProfitColor(profit || 0)}`}>
                                {formatProfit(profit || 0)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Row 4: copy settings button */}
                      <div className="pl-9">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-3 text-[10px] border-[#00FF41] text-[#00FF41] hover:bg-[#00FF41] hover:text-black w-full"
                          onClick={() => handleViewSettings(u)}
                          data-testid={`view-settings-mobile-${index}`}
                        >
                          <Copy className="h-3 w-3 mr-1" />
                          COPY SETTINGS
                        </Button>
                      </div>
                    </div>
                    
                    {/* DESKTOP LAYOUT — original 12-col grid */}
                    <div className="hidden md:grid grid-cols-12 gap-2 items-center">
                      {/* Rank */}
                      <div className="col-span-1">
                        <span className={`text-sm font-mono ${index === 0 ? 'text-yellow-500' : index === 1 ? 'text-gray-400' : index === 2 ? 'text-amber-600' : 'text-[#008F11]'}`}>
                          #{index + 1}
                        </span>
                      </div>
                      
                      {/* User Info */}
                      <div className="col-span-2 flex items-center gap-2 cursor-pointer" onClick={() => navigate(`/profile/${u.user_id}`)} data-testid={`user-profile-link-${index}`}>
                        {u.picture ? (
                          <img 
                            src={u.picture} 
                            alt={u.name} 
                            className="h-8 w-8 rounded-full border border-[#003B00]"
                          />
                        ) : (
                          <div className="h-8 w-8 rounded-full border border-[#003B00] flex items-center justify-center bg-[#001500]">
                            <User className="h-4 w-4 text-[#008F11]" />
                          </div>
                        )}
                        <div>
                          <div className="text-[#00FF41] text-sm font-medium truncate max-w-[120px] hover:underline">
                            {u.name || 'Unknown'}
                          </div>
                          <div className="text-xs truncate max-w-[120px]">
                            <span className={u.mode === 'live' ? 'text-red-500 font-bold' : 'text-blue-400 font-bold'}>
                              {u.mode?.toUpperCase() || 'SIM'}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Trading Status */}
                      <div className="col-span-1 flex justify-center items-center gap-1">
                        {u.is_trading ? (
                          <div className="flex items-center gap-1">
                            <Circle className="h-3 w-3 fill-[#00FF41] text-[#00FF41] animate-pulse" />
                            <span className="text-[#00FF41] text-xs">LIVE</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <Circle className="h-3 w-3 fill-[#003B00] text-[#003B00]" />
                            <span className="text-[#003B00] text-xs">IDLE</span>
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRestartBot(u.user_id, u.name);
                                }}
                                disabled={restartingUser === u.user_id}
                                className="h-5 w-5 p-0 ml-1 text-yellow-500 hover:text-yellow-400 hover:bg-yellow-900/20"
                                title="Restart this user's bot"
                              >
                                {restartingUser === u.user_id ? (
                                  <RefreshCw className="h-3 w-3 animate-spin" />
                                ) : (
                                  <Play className="h-3 w-3" />
                                )}
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Portfolio Value */}
                      <div className="col-span-2 text-right">
                        <span className="text-[#00FF41] text-sm font-mono">
                          ${u.portfolio_value?.toLocaleString() || '1,000.00'}
                        </span>
                      </div>
                      
                      {/* Total Profit/Hr */}
                      <div className="col-span-2 text-right">
                        <div className={`flex items-center justify-end gap-1 ${getProfitColor(u.profit_per_hour || 0)}`}>
                          {(u.profit_per_hour || 0) > 0 ? (
                            <TrendingUp className="h-3 w-3" />
                          ) : (u.profit_per_hour || 0) < 0 ? (
                            <TrendingDown className="h-3 w-3" />
                          ) : null}
                          <span className="text-sm font-mono">
                            {formatProfit(u.profit_per_hour || 0)}
                          </span>
                        </div>
                      </div>
                      
                      {/* Profit by Coin */}
                      <div className="col-span-2 flex justify-center gap-2 flex-wrap">
                        {u.profit_by_coin && Object.keys(u.profit_by_coin).length > 0 ? (
                          Object.entries(u.profit_by_coin).map(([coin, profit]) => (
                            <div key={coin} className="text-center">
                              <div className="text-[#003B00] text-[10px]">
                                {coin.replace('-USD', '')}
                              </div>
                              <div className={`text-xs font-mono ${getProfitColor(profit || 0)}`}>
                                {formatProfit(profit || 0)}
                              </div>
                            </div>
                          ))
                        ) : (
                          <span className="text-[#003B00] text-xs">--</span>
                        )}
                      </div>
                      
                      {/* View Settings Button */}
                      <div className="col-span-2 flex justify-center">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs border-[#00FF41] text-[#00FF41] hover:bg-[#00FF41] hover:text-black"
                          onClick={() => handleViewSettings(u)}
                          data-testid={`view-settings-${index}`}
                        >
                          <Copy className="h-3 w-3 mr-1" />
                          COPY
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 md:gap-6 text-xs text-[#008F11]">
        <div className="flex items-center gap-2">
          <Circle className="h-3 w-3 fill-[#00FF41] text-[#00FF41]" />
          <span>Actively Trading</span>
        </div>
        <div className="flex items-center gap-2">
          <Circle className="h-3 w-3 fill-[#003B00] text-[#003B00]" />
          <span>Idle</span>
        </div>
        <div className="flex items-center gap-2">
          <TrendingUp className="h-3 w-3 text-[#00FF41]" />
          <span>Positive Profit</span>
        </div>
        <div className="flex items-center gap-2">
          <TrendingDown className="h-3 w-3 text-red-500" />
          <span>Negative Profit</span>
        </div>
      </div>

      {/* Settings Modal */}
      {showSettingsModal && selectedUserSettings && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => setShowSettingsModal(false)}>
          <div 
            className="bg-[#0a0a0a] border border-[#00FF41] p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-[#00FF41] text-lg font-bold">
                {selectedUserSettings.name}'s Settings
              </h3>
              <Button
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0 text-[#00FF41] hover:bg-[#003B00]"
                onClick={() => setShowSettingsModal(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="space-y-3 mb-6">
              <div className="flex justify-between py-2 border-b border-[#003B00]">
                <span className="text-[#008F11]">RSI Oversold (Buy)</span>
                <span className="text-[#00FF41] font-mono">{selectedUserSettings.settings?.rsi_oversold || 25}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-[#003B00]">
                <span className="text-[#008F11]">RSI Overbought (Sell)</span>
                <span className="text-[#00FF41] font-mono">{selectedUserSettings.settings?.rsi_overbought || 75}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-[#003B00]">
                <span className="text-[#008F11]">Trade %</span>
                <span className="text-[#00FF41] font-mono">{selectedUserSettings.settings?.trade_percent || 10}%</span>
              </div>
              <div className="flex justify-between py-2 border-b border-[#003B00]">
                <span className="text-[#008F11]">No-Loss Sell</span>
                <span className="text-[#00FF41] font-mono">{selectedUserSettings.settings?.no_loss_sell_enabled !== false ? 'ON' : 'OFF'}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-[#003B00]">
                <span className="text-[#008F11]">Concentration Limit</span>
                <span className="text-[#00FF41] font-mono">
                  {selectedUserSettings.settings?.concentration_limit_enabled !== false 
                    ? `${selectedUserSettings.settings?.concentration_limit_percent || 75}%` 
                    : 'OFF'}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-[#003B00]">
                <span className="text-[#008F11]">Active Coins</span>
                <span className="text-[#00FF41] font-mono text-xs">
                  {selectedUserSettings.settings?.active_pairs?.map(p => p.replace('-USD', '')).join(', ') || 'BTC'}
                </span>
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button
                className="flex-1 matrix-btn rounded-none"
                onClick={handleCopySettings}
              >
                <Copy className="h-4 w-4 mr-2" />
                COPY THESE SETTINGS
              </Button>
              <Button
                variant="outline"
                className="border-[#003B00] text-[#008F11] hover:bg-[#001500]"
                onClick={() => setShowSettingsModal(false)}
              >
                CANCEL
              </Button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default UsersPage;
